import { useEventListener, useTimeoutFn } from "@vueuse/core";
import { useHostElement } from "#ce/composables/useHostElement";

const SLIDER_DEBOUNCE_MS = 500;

/**
 * Applies the distance slider's commits to the filter's distance. A native range commits on every arrow-key
 * step, so key steps wait:
 *   - key step:            the last of a run applies `SLIDER_DEBOUNCE_MS` after it
 *   - focus leaves slider: a waiting step applies at once (the required filters modal reads it on Continue)
 *   - its form submits:    a waiting step applies before the form's submit listeners read it
 *   - drag or track click: applies at once, after any waiting step
 *   - restore or clear:    drops a waiting step
 *
 * @param {import("vue").Ref<number>} selectedDistance the filter's applied distance
 * @param {import("vue").ShallowRef<HTMLElement | null>} slider the `sp-number-slider` that shows the distance
 */
export function useDistanceSliderCommit(selectedDistance, slider) {
  const host = useHostElement();
  let stepFromKeyboard = false;
  let pendingDistance;
  const { start: scheduleSliderDistance, stop: cancelSliderDistance } = useTimeoutFn(
    applySliderDistance,
    SLIDER_DEBOUNCE_MS,
    { immediate: false },
  );

  // On `window` so it runs before any listener on the form itself.
  useEventListener(window, "submit", applyBeforeOwnFormSubmits, { capture: true });

  function markKeyboardStep() {
    stepFromKeyboard = true;
  }

  function markPointerStep() {
    stepFromKeyboard = false;
    applySliderDistance();
  }

  function commitSliderDistance(distance) {
    pendingDistance = distance;

    if (stepFromKeyboard) {
      scheduleSliderDistance();
    } else {
      applySliderDistance();
    }
  }

  function applyBeforeOwnFormSubmits(event) {
    if (event.target === host?.form) {
      applySliderDistance();
    }
  }

  function applySliderDistance() {
    cancelSliderDistance();
    if (pendingDistance === undefined) {
      return;
    }
    selectedDistance.value = pendingDistance;
    pendingDistance = undefined;
  }

  function dropPendingSliderDistance() {
    if (pendingDistance === undefined) {
      return;
    }
    cancelSliderDistance();
    pendingDistance = undefined;

    // The slider still shows the dropped step, and a restore to the applied distance doesn't trigger the
    // update that would reset it.
    if (slider.value) {
      slider.value.value = selectedDistance.value;
    }
  }

  return {
    markKeyboardStep,
    markPointerStep,
    commitSliderDistance,
    applySliderDistance,
    dropPendingSliderDistance,
  };
}
